import { Unit } from "types/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { Row } from "views/applications-id-report/components/Row";
import { useState } from "react";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { useFormTyped } from "shared/hooks";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import addressApi from "api/landlord/address";
import { PlanForm } from "shared/app/PlanForm";

interface PlanSectionProps {
  unit: Unit;
}

const EditPlanForm = ({
  onClose,
  unit,
  initialValues
}: {
  onClose: () => void;
  unit: Unit;
  initialValues: {
    sqft?: number;
    beds?: number;
    baths?: number;
  };
}) => {
  const queryClient = useQueryClient();
  const { register, onSubmit, values } = useFormTyped(initialValues);

  const { mutate: updatePlan, isPending } = useMutation({
    mutationFn: () =>
      addressApi.editUnit(unit.id, {
        plan: {
          sqft: values.sqft,
          beds: values.beds,
          baths: values.baths
        }
      }),
    onSuccess: () => {
      toast.success("Plan updated");
      queryClient.invalidateQueries({
        queryKey: ["unit", String(unit.property.id), String(unit.id)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update plan: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: () => updatePlan()
  });

  return (
    <PlanForm
      register={register}
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveLoading={isPending}
        />
      }
    />
  );
};

export const PlanSection = ({ unit }: PlanSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing)
    return (
      <EditPlanForm
        onClose={() => setIsEditing(false)}
        unit={unit}
        initialValues={{
          sqft: unit.plan.sqft,
          beds: unit.plan.beds,
          baths: unit.plan.baths
        }}
      />
    );

  return (
    <SectionCard
      title="Plan"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4">
        <Row title="Size, sq.ft" value={unit.plan.sqft} />
        <Row title="beds" value={unit.plan.beds} />
        <Row title="baths" value={unit.plan.baths} />
      </div>
    </SectionCard>
  );
};
